import { useMemo } from 'react';

import { WalletIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ContactDebt, ContactDebtRole, DebtPayment, DebtType } from '@/shared/types/contacts';
import { formatDate, parseTimestamp } from '@/util/dateUtils';

import { useContactDebts } from '../hooks/useContactDebts';
import { useContactIdentities } from '../hooks/useContactIdentities';

interface DebtsTabProps {
  contactId: string;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;
type FieldTone = 'default' | 'muted' | 'warning' | 'destructive' | 'success';

interface DebtField {
  key: string;
  label: string;
  value: string;
  tone?: FieldTone;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const SEVERE_DPD_DAYS = 90;
const DATE_FORMAT = 'MMM d, yyyy';

export const DEBT_TYPE_KEY: Record<DebtType, string> = {
  credit_card: 'contacts.debts.type.credit_card',
  medical: 'contacts.debts.type.medical',
  auto_loan: 'contacts.debts.type.auto_loan',
  student_loan: 'contacts.debts.type.student_loan',
  personal_loan: 'contacts.debts.type.personal_loan',
  utility: 'contacts.debts.type.utility',
  mortgage: 'contacts.debts.type.mortgage',
  other: 'contacts.debts.type.other',
};

export const ROLE_KEY: Record<ContactDebtRole, string> = {
  primary_borrower: 'contacts.debts.role.primary_borrower',
  co_borrower: 'contacts.debts.role.co_borrower',
  guarantor: 'contacts.debts.role.guarantor',
  authorized_contact: 'contacts.debts.role.authorized_contact',
};

const FREQUENCY_KEY: Record<NonNullable<DebtPayment['frequency']>, string> = {
  weekly: 'contacts.debts.frequency_value.weekly',
  biweekly: 'contacts.debts.frequency_value.biweekly',
  monthly: 'contacts.debts.frequency_value.monthly',
};

const PAYMENT_STATUS_KEY: Record<NonNullable<DebtPayment['status']>, string> = {
  active: 'contacts.debts.payment_status_value.active',
  paused: 'contacts.debts.payment_status_value.paused',
  completed: 'contacts.debts.payment_status_value.completed',
  defaulted: 'contacts.debts.payment_status_value.defaulted',
  expired: 'contacts.debts.payment_status_value.expired',
  rejected: 'contacts.debts.payment_status_value.rejected',
};

export const TONE_CLASS: Record<FieldTone, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  warning: 'font-medium text-warning',
  destructive: 'font-medium text-destructive',
  success: 'font-medium text-success',
};

export const formatMoney = (amount?: number, currency?: string): string | null => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return null;
  }
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    currencyDisplay: 'narrowSymbol',
  }).format(amount);
};

export const formatDueDate = (value?: string | number): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return parseTimestamp(value) ? formatDate(value, DATE_FORMAT) : null;
};

export const computeDpd = (dueDate?: string): number | null => {
  const parsed = dueDate ? parseTimestamp(dueDate) : null;
  if (!parsed) {
    return null;
  }
  return Math.max(0, Math.floor((Date.now() - parsed.getTime()) / DAY_MS));
};

export const dpdTone = (dpd: number): FieldTone => {
  if (dpd >= SEVERE_DPD_DAYS) {
    return 'destructive';
  }
  if (dpd > 0) {
    return 'warning';
  }
  return 'default';
};

export const debtInfoFields = (debt: ContactDebt, t: TFn): DebtField[] => {
  const na = t('common.na');
  return [
    {
      key: 'debt_amount',
      label: t('contacts.debts.debt_amount'),
      value: formatMoney(debt.original_balance, debt.currency) ?? na,
    },
    {
      key: 'type',
      label: t('contacts.debts.type_label'),
      value: debt.debt_type ? t(DEBT_TYPE_KEY[debt.debt_type]) : na,
    },
    { key: 'creditor', label: t('contacts.debts.creditor'), value: debt.original_creditor || na },
    {
      key: 'date_of_origin',
      label: t('contacts.debts.date_of_origin'),
      value: formatDueDate(debt.created) ?? na,
    },
  ];
};

export const paymentGroupFields = (debt: ContactDebt, t: TFn): DebtField[] => {
  const na = t('common.na');
  const dpd = computeDpd(debt.due_date);
  const fields: DebtField[] = [
    {
      key: 'dpd',
      label: t('contacts.debts.dpd'),
      value: dpd === null ? na : t('contacts.debts.dpd_days', { 0: dpd }),
      tone: dpd === null ? 'default' : dpdTone(dpd),
    },
    {
      key: 'last_payment_date',
      label: t('contacts.debts.last_payment_date'),
      value: formatDueDate(debt.metadata?.last_payment_date as string) ?? na,
    },
  ];

  const payment = debt.payment;
  if (!payment) {
    return fields;
  }

  const statusValue = payment.status ? t(PAYMENT_STATUS_KEY[payment.status]) : null;

  if (payment.type === 'ptp') {
    fields.push({
      key: 'promise_to_pay',
      label: t('contacts.debts.promise_to_pay'),
      value: debt.current_creditor ?? debt.original_creditor ?? na,
    });
    const promiseDate = formatDueDate(debt.ptp_due_date ?? payment.agreed_at);
    if (promiseDate) {
      fields.push({ key: 'ptp_date', label: t('contacts.debts.ptp_date'), value: promiseDate });
    }
    fields.push({
      key: 'ptp_amount',
      label: t('contacts.debts.ptp_amount'),
      value: formatMoney(payment.promised_amount, debt.currency) ?? na,
    });
    if (statusValue) {
      fields.push({
        key: 'payment_status',
        label: t('contacts.debts.payment_status'),
        value: statusValue,
      });
    }
  }

  if (payment.type === 'plan') {
    fields.push({
      key: 'installment_amount',
      label: t('contacts.debts.installment_amount'),
      value: formatMoney(payment.installment_amount, debt.currency) ?? na,
    });
    if (payment.frequency) {
      fields.push({
        key: 'frequency',
        label: t('contacts.debts.frequency'),
        value: t(FREQUENCY_KEY[payment.frequency]),
      });
    }
    if (typeof payment.total_installments === 'number') {
      fields.push({
        key: 'installments',
        label: t('contacts.debts.installments'),
        value: `${payment.installments_paid ?? 0}/${payment.total_installments}`,
      });
    }
    const nextDue = formatDueDate(payment.next_due_date);
    if (nextDue) {
      fields.push({ key: 'next_payment', label: t('contacts.debts.next_payment'), value: nextDue });
    }
    if (statusValue) {
      fields.push({
        key: 'payment_status',
        label: t('contacts.debts.payment_status'),
        value: statusValue,
      });
    }
  }

  if (payment.type === 'settlement') {
    fields.push({
      key: 'settlement_offer',
      label: t('contacts.debts.settlement_offer'),
      value: formatMoney(payment.offer_amount, debt.currency) ?? na,
    });
    const expiry = formatDueDate(payment.offer_expiry);
    if (expiry) {
      fields.push({ key: 'expires', label: t('contacts.debts.expires'), value: expiry });
    }
    if (statusValue) {
      fields.push({
        key: 'payment_status',
        label: t('contacts.debts.payment_status'),
        value: statusValue,
      });
    }
  }

  fields.push({
    key: 'reduction_discount',
    label: t('contacts.debts.reduction_discount'),
    value: typeof payment.offer_percentage === 'number' ? `${payment.offer_percentage}%` : '-',
  });

  return fields;
};

const contactGroupFields = (
  debt: ContactDebt,
  primaryIdentityValue: string | undefined,
  t: TFn
): DebtField[] => {
  const fields: DebtField[] = [];
  if (debt.contact_role) {
    fields.push({ key: 'contact_role', label: t(ROLE_KEY[debt.contact_role]), value: '-' });
  }
  if (primaryIdentityValue) {
    fields.push({
      key: 'identity',
      label: t('contacts.debts.identity'),
      value: primaryIdentityValue,
    });
  }
  return fields;
};

export const FieldGroup = ({ fields, className }: { fields: DebtField[]; className?: string }) => (
  <div className={cn('flex flex-col gap-4 px-1 py-2', className)}>
    {fields.map((field) => (
      <div key={field.key} className="flex items-baseline justify-between gap-4">
        <span className="text-sm leading-5 text-muted-foreground">{field.label}</span>
        <span className={cn('text-right text-sm leading-5', TONE_CLASS[field.tone ?? 'default'])}>
          {field.value}
        </span>
      </div>
    ))}
  </div>
);

const DebtAccordionItem = ({
  debt,
  title,
  primaryIdentityValue,
}: {
  debt: ContactDebt;
  title: string;
  primaryIdentityValue: string | undefined;
}) => {
  const { t } = useTranslation();
  const info = debtInfoFields(debt, t);
  const payment = paymentGroupFields(debt, t);
  const contact = contactGroupFields(debt, primaryIdentityValue, t);

  return (
    <AccordionItem
      value={debt.debt_id}
      className="group rounded-lg border border-border bg-background last:border-b"
    >
      <AccordionTrigger className="px-5 py-4 hover:no-underline">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-sm text-muted-foreground group-data-[state=open]:hidden">
            {formatMoney(debt.original_balance, debt.currency) ?? t('common.na')}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-2 px-5 pb-4">
        <FieldGroup fields={info} />
        <Separator />
        <FieldGroup fields={payment} />
        {contact.length > 0 && (
          <>
            <Separator />
            <FieldGroup fields={contact} />
          </>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

const TotalDebtBar = ({ debts }: { debts: ContactDebt[] }) => {
  const { t } = useTranslation();
  const total = useMemo(() => {
    const na = t('common.na');
    const currencies = new Set(debts.map((d) => d.currency).filter(Boolean));
    if (currencies.size !== 1) {
      return na;
    }
    const currency = debts[0]?.currency;
    const sum = debts.reduce((acc, d) => acc + (d.original_balance ?? 0), 0);
    return formatMoney(sum, currency) ?? na;
  }, [debts, t]);

  return (
    <div className="rounded-xl border border-border bg-muted/50 p-1">
      <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
        <span className="text-sm font-medium text-foreground">
          {t('contacts.debts.total_debt')}
        </span>
        <span className="text-sm text-muted-foreground">{total}</span>
      </div>
    </div>
  );
};

export const DebtsTab = ({ contactId }: DebtsTabProps) => {
  const { debts, status } = useContactDebts(contactId);
  const { identities } = useContactIdentities(contactId);
  const { t } = useTranslation();

  const primaryIdentityValue = useMemo(() => {
    const identity = identities.find((i) => i.type === 'phone' || i.type === 'email');
    return identity?.value;
  }, [identities]);

  const debtTitles = useMemo(() => {
    const typeTotal = new Map<string, number>();
    for (const debt of debts) {
      if (debt.debt_type) {
        typeTotal.set(debt.debt_type, (typeTotal.get(debt.debt_type) ?? 0) + 1);
      }
    }
    const typeIndex = new Map<string, number>();
    return debts.map((debt, i) => {
      if (!debt.debt_type) {
        return t('contacts.debts.debt_number', { 0: i + 1 });
      }
      const label = t(DEBT_TYPE_KEY[debt.debt_type]);
      if ((typeTotal.get(debt.debt_type) ?? 0) > 1) {
        const idx = (typeIndex.get(debt.debt_type) ?? 0) + 1;
        typeIndex.set(debt.debt_type, idx);
        return `${label} (${idx})`;
      }
      return label;
    });
  }, [debts, t]);

  if (status === 'pending') {
    return (
      <div className="mt-6 space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (debts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted/50">
          <WalletIcon size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{t('contacts.debts.empty_title')}</h3>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {t('contacts.debts.empty_description')}
        </p>
      </div>
    );
  }

  const defaultOpen = debts[0] ? [debts[0].debt_id] : [];

  return (
    <div className="mt-3 flex w-full flex-col gap-4 pb-8">
      <TotalDebtBar debts={debts} />
      <h3 className="text-sm font-medium text-muted-foreground">
        {t('contacts.debts.debt_analysis')}
      </h3>
      <Accordion type="multiple" defaultValue={defaultOpen} className="flex flex-col gap-3">
        {debts.map((debt, index) => (
          <DebtAccordionItem
            key={debt.debt_id}
            debt={debt}
            title={debtTitles[index]}
            primaryIdentityValue={primaryIdentityValue}
          />
        ))}
      </Accordion>
    </div>
  );
};
